import { Field, Input, Label, RadioGroup } from "@headlessui/react";
import { useTranslate } from "@lobby/ocb-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Payment } from "../../../../../entities";
import {
  APIError,
  Button,
  Icon,
  Spinner,
  type TPaymentMethodsList as PaymentMethodsList,
  emitter,
  first,
  isEmptyArray,
} from "../../../../../shared";
import { NoDataPlaceholder, ProfileSection } from "../../components";
import { PaymentMethodCard } from "./components";
import {
  createExpireYearPattern,
  penniesToDollars,
  removeNonNumeric,
} from "./helpers";
import * as Sentry from "@sentry/react";
import { clsx } from "clsx";

const expireYearPattern = createExpireYearPattern();

const paymentCardKeys = [
  "name",
  "cardNumber",
  "expireMonth",
  "expireYear",
  "cardCvv",
] as const;

type PaymentMethod = PaymentMethodsList["data"][number] & {
  paymentFields?: PaymentField[];
};

type PaymentMethodType = PaymentMethod["type"];
type PaymentCardKeys = (typeof paymentCardKeys)[number];

type PaymentField = {
  key: PaymentCardKeys;
  label: string;
  validation: Partial<{
    type: string;
    required: boolean;
    pattern: string;
    minLength: number;
    maxLength: number;
  }>;
  options: unknown[];
};

type QRCodeInstructions = {
  qrcodeImage: string;
  paymentCode: string;
};

export function DepositTab() {
  const { $t } = useTranslate();

  const { data, isLoading } = Payment.useMethodList("deposit");

  return (
    <Sentry.ErrorBoundary fallback={<pre>Unknown error</pre>}>
      <ProfileSection
        isLoading={isLoading}
        data={data}
        placeholder={
          <NoDataPlaceholder
            topText={$t({
              defaultMessage:
                "Currently, there are no available methods for depositing funds.",
            })}
            bottomText={$t({
              defaultMessage:
                "Please contact the support chat to deposit funds.",
            })}
            icon={<Icon name="creditCard" className="text-[6.25rem]" />}
          />
        }
      >
        {(validData) => <DepositTabView data={validData} />}
      </ProfileSection>
    </Sentry.ErrorBoundary>
  );
}

function DepositTabView({ data }: { data: PaymentMethod[] }) {
  const { $t } = useTranslate();
  const [paymentMethod, setPaymentMethod] = useState(first(data)?.id);
  const [paymentAmount, setPaymentAmount] = useState<string>("");
  const [validationError, setValidationError] = useState("");
  const [QRCodeInstructions, setQRCodeInstructions] =
    useState<QRCodeInstructions | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const { mutate: deposit, isPending } = Payment.useDeposit();

  const selectedMethod = getSelectedMethod(paymentMethod) ?? data[0];

  const isCardFormVisible =
    selectedMethod.type === "card" &&
    !isEmptyArray(selectedMethod?.paymentFields);

  const additionalFieldsToRender = useMemo(() => {
    const paymentFields = selectedMethod?.paymentFields;
    if (!paymentFields || isEmptyArray(paymentFields)) {
      return [];
    }

    return paymentFields.filter(({ key }) => !paymentCardKeys.includes(key));
  }, [selectedMethod]);

  let paymentAmountMin;
  let paymentAmountMax;
  if (selectedMethod.limits) {
    paymentAmountMin = penniesToDollars(selectedMethod.limits.min);
    paymentAmountMax = penniesToDollars(selectedMethod.limits.max);
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!formRef.current) {
      throw new Error("Form reference is not set");
    }

    setValidationError("");

    const data = new FormData(formRef.current);

    data.set(
      "payment-amount",
      String(Number(data.get("payment-amount")) * 100)
    );

    if (validateForm(data)) {
      const methodData = prepareMethodData(selectedMethod.type, data) as any;

      deposit(
        {
          methodId: Number(data.get("payment-method")),
          amount: Number(data.get("payment-amount")),
          methodData,
        },
        {
          onSuccess: ({ error, result }) => {
            setQRCodeInstructions(null);

            if (error) {
              const { message, code } = error;
              emitter.emit("ERROR", { error: new APIError(message, { code }) });
            } else if (result?.paymentUrl) {
              if (result.paymentUrlType === "url") {
                window.location.href = result.paymentUrl;
              } else if (
                result.paymentUrlType === "qrcode" &&
                result.paymentCode
              ) {
                setQRCodeInstructions({
                  qrcodeImage: result.paymentUrl,
                  paymentCode: result.paymentCode,
                });
              }
            }
          },
        }
      );
    } else {
      console.error("Validation error");
    }
  }

  function prepareMethodData(methodType: PaymentMethodType, data: FormData) {
    switch (methodType) {
      case "card":
        return preparePaymentFields(data);
      default:
        return {};
    }
  }

  function checkIfAdditionalFieldsExist(fields: PaymentField[]) {
    return fields.some(({ key }) => !paymentCardKeys.includes(key));
  }

  function preparePaymentFields(data: FormData) {
    const paymentFields = selectedMethod?.paymentFields ?? null;

    const additionalFields: Record<string, unknown> = {};
    if (paymentFields && checkIfAdditionalFieldsExist(paymentFields)) {
      for (const field of paymentFields) {
        if (
          field.key &&
          !paymentCardKeys.includes(field.key) &&
          data.has(field.key)
        ) {
          additionalFields[field.key] = data.get(field.key);
        }
      }
    }

    return {
      cardNumber: String(data.get("cardNumber")).replace(/\s/g, ""),
      expireMonth: Number(data.get("expireMonth")),
      expireYear: Number(data.get("expireYear")),
      cardCvv: Number(data.get("cardCvv")),
      name: String(data.get("name")).trim(),
      ...additionalFields,
    };
  }

  function validatePaymentMethod(method: number) {
    return data.some((item) => item.id === method);
  }

  function getSelectedMethod(method: number | undefined) {
    return data.find((item) => item.id === Number(method));
  }

  function validatePaymentAmount(amount: number, method: number) {
    const selectedMethod = data.find((item) => item.id === method);

    if (!selectedMethod) return false;

    if (Boolean(selectedMethod.limits)) {
      const min = selectedMethod.limits?.min ?? 0;
      const max = selectedMethod.limits?.max ?? 0;

      return amount && amount >= min && amount <= max;
    }
    return amount && amount > 0;
  }

  function validateForm(data: FormData) {
    const paymentMethod = Number(data.get("payment-method"));
    const paymentAmount = Number(data.get("payment-amount"));

    return (
      validatePaymentMethod(paymentMethod) &&
      validatePaymentAmount(paymentAmount, paymentMethod)
    );
  }

  useEffect(() => {
    const defaultAmount = selectedMethod.limits?.default;
    if (defaultAmount !== undefined) {
      setPaymentAmount(String(penniesToDollars(defaultAmount)));
    }
  }, [paymentMethod]);

  return (
    <div className="gutter-stable relative min-h-0 py-1 mobile-only:pb-5 lg:h-full lg:max-h-full lg:overflow-y-auto">
      <form
        className={`text-14 ${isPending ? "blur-sm" : ""}`}
        onSubmit={handleSubmit}
        ref={formRef}
      >
        <ol className="list-outside list-decimal space-y-10 ps-5">
          <li>
            {$t({ defaultMessage: "Choose one of the payment methods" })}
            <RadioGroup
              value={paymentMethod}
              onChange={setPaymentMethod}
              name="payment-method"
              className="mt-2.5"
            >
              <ul className="flex flex-wrap gap-2.5">
                {data.map(({ id, name, logoUrl, commission, limits }, i) => (
                  <li key={i}>
                    <PaymentMethodCard
                      id={id}
                      name={name}
                      logoUrl={logoUrl}
                      commission={commission}
                      limits={limits}
                    />
                  </li>
                ))}
              </ul>
            </RadioGroup>
          </li>
          {isCardFormVisible && (
            <li>
              {$t({ defaultMessage: "Enter your card details" })}

              <div className="space-y-2 max-w-[13.75rem]">
                <input
                  type="text"
                  name="cardNumber"
                  inputMode="numeric"
                  placeholder={$t({ defaultMessage: "Card number" })}
                  pattern="(\d[ ]?){13,19}"
                  autoComplete="cc-number"
                  maxLength={23}
                  minLength={13}
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;

                    let value = removeNonNumeric(target.value);
                    value = value.replace(/(\d{4})(?=\d)/g, "$1 ");

                    if (value !== target.value) {
                      target.value = value;
                    }
                  }}
                  required
                  className="w-full"
                />
                <div className="grid grid-cols-12 gap-1">
                  <input
                    type="text"
                    name="expireMonth"
                    inputMode="numeric"
                    pattern="(0[1-9]|1[0-2])"
                    minLength={2}
                    maxLength={2}
                    placeholder={$t({ defaultMessage: "MM" })}
                    autoComplete="cc-exp-month"
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = removeNonNumeric(target.value);
                    }}
                    required
                    className="col-span-3"
                  />
                  <input
                    type="text"
                    name="expireYear"
                    inputMode="numeric"
                    pattern={expireYearPattern}
                    minLength={2}
                    maxLength={2}
                    placeholder={$t({ defaultMessage: "YY" })}
                    autoComplete="cc-exp-year"
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = removeNonNumeric(target.value);
                    }}
                    required
                    className="col-span-3"
                  />
                  <input
                    type="text"
                    name="cardCvv"
                    inputMode="numeric"
                    pattern="\d{3}"
                    maxLength={3}
                    placeholder={$t({ defaultMessage: "CVC" })}
                    autoComplete="cc-csc"
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = removeNonNumeric(target.value);
                    }}
                    required
                    className="col-span-3 col-start-10"
                  />
                </div>
                <input
                  type="text"
                  name="name"
                  placeholder={$t({ defaultMessage: "Cardholder name" })}
                  pattern="\D{5,100}"
                  autoComplete="cc-name"
                  onInput={(e) => {
                    const target = e.target as HTMLInputElement;
                    target.value = target.value.trimStart();
                  }}
                  required
                  className="w-full [&:not(:placeholder-shown)]:uppercase"
                />

                {additionalFieldsToRender.map((field) => (
                  <input
                    name={field.key}
                    placeholder={field.label}
                    className="w-full"
                    {...field.validation}
                  />
                ))}
              </div>
            </li>
          )}
          <li>
            {$t({ defaultMessage: "Enter the amount you want to deposit" })}
            <Field disabled={!selectedMethod}>
              <Label className="data-[disabled]:opacity-50">
                {$t({ defaultMessage: "Amount" })}:
              </Label>
              <div className="flex mobile-only:flex-col gap-3 lg:gap-2.5 lg:items-center">
                <Input
                  className="w-52 appearance-none data-[disabled]:opacity-50"
                  placeholder={$t({ defaultMessage: "Enter the amount" })}
                  name="payment-amount"
                  type="number"
                  inputMode="numeric"
                  value={paymentAmount}
                  // pattern="[0-9]*"
                  // onChange remove all non-numeric characters
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  min={paymentAmountMin}
                  max={paymentAmountMax}
                  step={0.01}
                  required
                />
                <Button
                  variant="success"
                  type="submit"
                  disabled={!paymentAmount}
                  className="mobile-only:mx-auto mobile-only:w-fit"
                >
                  {$t({ defaultMessage: "Deposit" })}
                </Button>
              </div>
            </Field>

            {validationError && (
              <p className="text-center text-alizarin-crimson leading-none">
                {validationError}
              </p>
            )}

            <div
              style={{
                color: "var(--text-secondary-color)",
              }}
              className="mt-2 text-12"
            >
              {$t({
                defaultMessage:
                  "After clicking 'Deposit', you will be redirected to the payment page.",
              })}
            </div>
          </li>
        </ol>
      </form>

      {QRCodeInstructions && (
        <PixPaymentInstructions
          qrcode={QRCodeInstructions.qrcodeImage}
          paymentCode={QRCodeInstructions.paymentCode}
          className="mt-5"
        />
      )}

      {isPending && (
        <div className="absolute inset-0 flex-center">
          <Spinner />
        </div>
      )}
    </div>
  );
}

interface PixPaymentInstructionsProps {
  className?: string;
  qrcode: string;
  paymentCode: string;
}

function PixPaymentInstructions({
  className,
  qrcode,
  paymentCode,
}: PixPaymentInstructionsProps) {
  const { $t } = useTranslate();
  const [copied, setCopied] = useState(false);
  const timeoutID = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(paymentCode);
    setCopied(true);

    if (timeoutID.current) {
      clearTimeout(timeoutID.current);
    }
    timeoutID.current = setTimeout(setCopied, 2000, false);
  };

  return (
    <div className={className}>
      <div className="mb-3 text-center lg:text-lg">
        {$t({ defaultMessage: "Choose a payment method" })}
      </div>
      <div className="flex gap-3 text-xs lg:text-sm">
        <div className="max-w-40">
          <div>{$t({ defaultMessage: "Scan the QR code:" })}</div>
          <div className="mt-1">
            <img
              src={`data:image/jpeg;base64,${qrcode}`}
              alt="QR Code"
              className="size-40 min-w-40"
            />
          </div>
        </div>
        <div className="min-w-0">
          <div>{$t({ defaultMessage: "Copy and paste ID:" })}</div>
          <Input
            type="text"
            value={paymentCode}
            readOnly
            className="outline-[var(--accent-color)] max-w-full mt-1"
          />
          <div className="relative w-fit">
            <Button variant="secondary" onClick={handleCopy} className="my-3">
              <span>{$t({ defaultMessage: "Copy ID" })}</span>
              <span
                className={clsx(
                  "border-[var(--accent-color)] border-1 -top-1 absolute left-1/2 bg-black -translate-x-1/2 -translate-y-1 bg-gray-800 text-xs rounded-rounded px-2 py-1",
                  copied
                    ? "opacity-100"
                    : "opacity-0 transition-opacity duration-300"
                )}
              >
                {$t({ defaultMessage: "Copied!" })}
              </span>
            </Button>
          </div>
          <div className="font-semibold">
            {$t({ defaultMessage: "Payment instructions with ID:" })}
          </div>
          <ol className="ps-5 mb-4 list-decimal">
            <li>{$t({ defaultMessage: "Copy the transaction ID" })}</li>
            <li>
              {$t({
                defaultMessage:
                  "Use the ID to identify your payment in your bank's app",
              })}
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
